import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { directoryApi, type CompanyInput, type PersonInput, type ProjectDirectory } from '@/lib/apiClient';

/**
 * The form fields the add and edit dialogs share (#598): a person, a company and
 * its CSI divisions. Each is controlled: the dialog owns the values.
 */

export const emptyPerson = (): PersonInput => ({ name: '', title: '', email: '', phone: '', cell: '', notes: '' });
export const emptyCompany = (name = '', kind = 'subcontractor'): CompanyInput => ({
  name, kind, phone: '', address1: '', address2: '', city: '', state: '', postalCode: '', website: '', notes: '', divisions: [],
});

export function PersonFields({ value, onChange, disabled }: { value: PersonInput; onChange: (v: PersonInput) => void; disabled?: boolean }) {
  const set = (k: keyof PersonInput) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <>
      <div className="dir-grid2">
        <label className="dir-field"><span>Name</span>
          <input value={value.name} onChange={set('name')} maxLength={200} disabled={disabled} required />
        </label>
        <label className="dir-field"><span>Title</span>
          <input value={value.title ?? ''} onChange={set('title')} maxLength={100} disabled={disabled} placeholder="Project Manager, Estimator…" />
        </label>
      </div>
      <div className="dir-grid3">
        <label className="dir-field"><span>Email</span>
          <input type="email" value={value.email ?? ''} onChange={set('email')} maxLength={254} disabled={disabled} />
        </label>
        <label className="dir-field"><span>Phone</span>
          <input type="tel" value={value.phone ?? ''} onChange={set('phone')} maxLength={40} disabled={disabled} />
        </label>
        <label className="dir-field"><span>Cell</span>
          <input type="tel" value={value.cell ?? ''} onChange={set('cell')} maxLength={40} disabled={disabled} />
        </label>
      </div>
      <label className="dir-field"><span>Notes</span>
        <textarea value={value.notes ?? ''} onChange={set('notes')} maxLength={1000} disabled={disabled} />
      </label>
    </>
  );
}

export function CompanyFields({ value, onChange, directory, disabled }: {
  value: CompanyInput; onChange: (v: CompanyInput) => void; directory: ProjectDirectory; disabled?: boolean;
}) {
  const set = (k: keyof CompanyInput) => (e: { target: { value: string } }) => onChange({ ...value, [k]: e.target.value });
  return (
    <>
      <div className="dir-grid2">
        <label className="dir-field"><span>Company</span>
          <input value={value.name} onChange={set('name')} maxLength={200} disabled={disabled} required />
        </label>
        <label className="dir-field"><span>Type</span>
          <select value={value.kind} onChange={set('kind')} disabled={disabled}>
            {directory.kinds.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
          </select>
        </label>
      </div>
      <div className="dir-grid2">
        <label className="dir-field"><span>Office phone</span>
          <input type="tel" value={value.phone ?? ''} onChange={set('phone')} maxLength={40} disabled={disabled} />
        </label>
        <label className="dir-field"><span>Website</span>
          <input value={value.website ?? ''} onChange={set('website')} maxLength={300} disabled={disabled} />
        </label>
      </div>
      <label className="dir-field"><span>Address</span>
        <input value={value.address1 ?? ''} onChange={set('address1')} maxLength={200} disabled={disabled} />
      </label>
      <label className="dir-field"><span>Address line 2</span>
        <input value={value.address2 ?? ''} onChange={set('address2')} maxLength={200} disabled={disabled} placeholder="Suite, unit, floor…" />
      </label>
      <div className="dir-grid3">
        <label className="dir-field"><span>City</span>
          <input value={value.city ?? ''} onChange={set('city')} maxLength={100} disabled={disabled} />
        </label>
        <label className="dir-field"><span>State</span>
          <input value={value.state ?? ''} onChange={set('state')} maxLength={40} disabled={disabled} />
        </label>
        <label className="dir-field"><span>ZIP</span>
          <input value={value.postalCode ?? ''} onChange={set('postalCode')} maxLength={20} disabled={disabled} />
        </label>
      </div>
      {/* "Company notes", not "Notes": the person's own Notes box is in the same dialog. */}
      <label className="dir-field"><span>Company notes</span>
        <textarea value={value.notes ?? ''} onChange={set('notes')} maxLength={1000} disabled={disabled} />
      </label>
      <DivisionPicker directory={directory} value={value.divisions ?? []} onChange={(divisions) => onChange({ ...value, divisions })} disabled={disabled} />
    </>
  );
}

/** The CSI MasterFormat divisions a company works in. */
export function DivisionPicker({ directory, value, onChange, disabled }: {
  directory: ProjectDirectory; value: string[]; onChange: (v: string[]) => void; disabled?: boolean;
}) {
  const on = new Set(value);
  const toggle = (code: string) => onChange(on.has(code) ? value.filter((c) => c !== code) : [...value, code].sort());
  return (
    <fieldset className="dir-field" style={{ border: 0, padding: 0, margin: 0 }}>
      <span>Trades (CSI divisions){value.length ? ` · ${value.length} picked` : ''}</span>
      <div className="dir-divpick">
        {directory.divisions.map((d) => (
          <label key={d.code}>
            <input type="checkbox" checked={on.has(d.code)} onChange={() => toggle(d.code)} disabled={disabled} />
            <span><strong>{d.code}</strong> {d.title}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Search the book for someone's company: a new person's in Add Contact, or a
 * new one for someone in Edit. Without it, someone new at a company already in
 * the book could only be added by going back to the other tab, and typing the
 * company in again as new was refused as a duplicate (review).
 */
export function BookCompanyPicker({ directory, onPick }: { directory: ProjectDirectory; onPick: (c: { id: number; name: string }) => void }) {
  const [typed, setTyped] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setTerm(typed.trim()), 250);
    return () => window.clearTimeout(t);
  }, [typed]);
  const q = useQuery({ queryKey: ['directory-search', term], queryFn: () => directoryApi.search(term), enabled: term.length >= 2 });
  // Companies by name, and the companies of people who match (search "sam" → Sam's company).
  const found = new Map<number, string>();
  for (const c of q.data?.companies ?? []) found.set(c.id, c.name);
  for (const p of q.data?.people ?? []) if (p.company) found.set(p.company.id, p.company.name);
  return (
    <>
      <label className="dir-field"><span>Find their company</span>
        <input type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Company name, or someone who works there" />
      </label>
      {term.length >= 2 && (
        q.isPending ? <p className="dir-muted dir-small">Searching…</p>
          : q.isError ? <div className="dir-error">Couldn’t search the address book. {(q.error as Error).message}</div>
            : found.size === 0 ? <p className="dir-muted dir-small">No company in the address book matches “{term}”. Add it as a new company.</p>
              : (
                <div className="dir-pick">
                  {[...found].map(([id, name]) => {
                    const kind = q.data.companies.find((c) => c.id === id)?.kind;
                    return (
                      <button key={id} type="button" className="dir-pick-row" onClick={() => onPick({ id, name })}>
                        <span><strong>{name}</strong></span>
                        <span className="dir-muted">{kind ? directory.kinds.find((k) => k.key === kind)?.label ?? kind : ''}</span>
                      </button>
                    );
                  })}
                </div>
              )
      )}
    </>
  );
}
